import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';

export default function WaterDamageFirst24Hours() {
  useEffect(() => {
    window.scrollTo(0, 0);

    const SITE_URL = 'https://mercedwaterrestoration.com';

    document.title = 'What to Do in the First 24 Hours After Water Damage | Merced CA | Indoor-Restore';

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', 'Acting fast after water damage is critical. Learn the immediate steps to minimize structural damage, prevent mold growth, and protect your belongings in Merced, California.');

    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', 'water damage first 24 hours, water damage Merced CA, emergency water damage response, flood damage steps, water damage restoration Central Valley');

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${SITE_URL}/blog/water-damage-first-24-hours`);

    // Add Schema.org JSON-LD for BlogPosting
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "What to Do in the First 24 Hours After Water Damage in Merced, CA",
      "description": "Acting fast after water damage is critical. Learn the immediate steps you should take to minimize structural damage, prevent mold growth, and protect your belongings in Merced, California.",
      "image": "https://public.readdy.ai/ai/img_res/cafff88e514106fad2f3fe21451e45c9.jpg",
      "datePublished": "2025-06-10T08:00:00-07:00",
      "dateModified": "2025-06-10T08:00:00-07:00",
      "author": {
        "@type": "Organization",
        "name": "Indoor-Restore Services",
        "url": SITE_URL
      },
      "publisher": {
        "@type": "Organization",
        "name": "Indoor-Restore Services",
        "logo": {
          "@type": "ImageObject",
          "url": "https://mercedwaterrestoration.com/wp-content/uploads/2024/11/Indoor-Restore-Services-Logo.png"
        }
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `${SITE_URL}/blog/water-damage-first-24-hours`
      },
      "articleBody": "Water damage can strike your Merced home without warning—whether from a burst pipe during a cold Central Valley winter night, a malfunctioning water heater, severe storm flooding, or an overflowing washing machine. When disaster hits, the first 24 hours are absolutely critical..."
    });
    document.head.appendChild(script);

    // Add FAQPage Schema
    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How quickly does mold grow after water damage in Merced, CA?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "In Merced's warm Central Valley climate, mold can begin growing within 24 to 48 hours after water exposure. The combination of moisture and our region's heat creates ideal conditions for rapid mold colonization. This is why immediate water extraction and structural drying are essential—waiting even a day or two can turn a manageable water damage situation into a costly mold remediation project."
          }
        },
        {
          "@type": "Question",
          "name": "Should I use my home's electricity after water damage?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "No. If water has reached electrical outlets, appliances, or your breaker box, do not use electricity in affected areas. Turn off power at the main breaker if it's safe to do so, or call PG&E and a licensed electrician. Electrocution is a serious risk in flooded homes. Indoor-Restore Services works closely with certified electricians throughout Merced County to ensure your home is safe before restoration begins."
          }
        },
        {
          "@type": "Question",
          "name": "Will my homeowner's insurance cover water damage in Merced?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "It depends on the source. Most homeowner's policies in California cover sudden and accidental water damage—like burst pipes, appliance malfunctions, or roof leaks. However, flood damage from natural disasters typically requires separate flood insurance (NFIP or private flood policies). Indoor-Restore Services has extensive experience working with insurance companies serving Merced, Atwater, Los Banos, and Livingston. We provide detailed documentation, moisture readings, and photos to support your claim and maximize your payout."
          }
        },
        {
          "@type": "Question",
          "name": "Can I dry out my Merced home myself with fans and dehumidifiers?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Consumer-grade fans and dehumidifiers are not sufficient for structural drying. Water penetrates deep into drywall, insulation, subfloors, and framing—areas you can't see or reach with household equipment. Professional restoration companies like Indoor-Restore Services use industrial-grade air movers, commercial dehumidifiers, and moisture meters to ensure complete drying. Incomplete drying leads to hidden mold growth, wood rot, and structural damage that can cost tens of thousands of dollars to repair later."
          }
        }
      ]
    });
    document.head.appendChild(faqScript);

    return () => {
      if (script.parentNode) document.head.removeChild(script);
      if (faqScript.parentNode) document.head.removeChild(faqScript);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Article Header */}
      <article className="pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
            <Link to="/" className="hover:text-teal-600 transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <Link to="/blog" className="hover:text-teal-600 transition-colors cursor-pointer">Blog</Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <span className="text-gray-900">Water Damage First 24 Hours</span>
          </nav>

          {/* Category & Date */}
          <div className="flex items-center gap-4 mb-4">
            <span className="bg-teal-50 text-teal-700 text-xs font-semibold px-3 py-1 rounded-full">
              Water Damage
            </span>
            <span className="text-gray-400 text-sm">June 10, 2025</span>
            <span className="text-gray-400 text-sm">• 5 min read</span>
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            What to Do in the First 24 Hours After Water Damage in Merced, CA
          </h1>

          {/* Featured Image */}
          <div className="w-full h-96 rounded-xl overflow-hidden mb-8">
            <img
              src="https://readdy.ai/api/search-image?query=flooded%20living%20room%20interior%20with%20water%20on%20hardwood%20floor%20furniture%20damaged%20homeowner%20assessing%20water%20damage%20natural%20light%20realistic%20photography&width=1200&height=600&seq=article-hero-indoor&orientation=landscape"
              alt="Water damage in Merced home living room with flooded hardwood floors"
              title="Water Damage Restoration Merced CA - First 24 Hours"
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* Article Content */}
          <div className="prose prose-lg max-w-none">
            {/* Introduction */}
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Water damage can strike your Merced home without warning—whether from a burst pipe during a cold Central Valley winter night, a malfunctioning water heater, severe storm flooding, or an overflowing washing machine. When disaster hits, <strong>the first 24 hours are absolutely critical</strong>. How you respond in those initial hours can mean the difference between a quick, affordable restoration and a prolonged, expensive nightmare involving mold remediation, structural repairs, and insurance headaches.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              As Merced County's trusted water damage restoration experts, <strong>Indoor-Restore Services</strong> has helped hundreds of homeowners in Merced, Atwater, Los Banos, Livingston, and surrounding communities recover from water emergencies. In this guide, we'll walk you through exactly what to do—step by step—in the first 24 hours after water damage to protect your home, your belongings, and your family's health.
            </p>

            {/* Hour-by-Hour Action Plan */}
            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 flex items-center gap-3">
              <i className="ri-time-line text-teal-600"></i>
              Your First 24 Hours: A Step-by-Step Action Plan
            </h2>

            <div className="bg-gradient-to-br from-teal-50 to-teal-100/50 border-l-4 border-teal-600 p-6 rounded-lg mb-8">
              <p className="text-sm text-teal-900 font-semibold mb-2">
                <i className="ri-alarm-warning-line mr-2"></i>EMERGENCY TIP
              </p>
              <p className="text-teal-800 text-sm leading-relaxed">
                If you're dealing with a water emergency right now, <strong>call Indoor-Restore Services immediately at (209) 451-2288</strong>. We offer 24/7 emergency response throughout Merced County and can be on-site within 60 minutes to begin water extraction and damage mitigation.
              </p>
            </div>

            <h3 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
              <span className="inline-flex items-center justify-center w-10 h-10 bg-teal-600 text-white rounded-full mr-3 text-lg">1</span>
              Ensure Safety First (Minutes 1–15)
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Before you do anything else, <strong>assess the safety of your environment</strong>. Water and electricity are a deadly combination. If water has reached electrical outlets, appliances, or your breaker panel, do not enter the area. Turn off electricity at the main breaker if you can do so safely, or call PG&E and wait for professional help.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              If the water damage is severe (more than a few inches of standing water), or if the water is contaminated (sewage backup, floodwater from outside), evacuate immediately and call professionals. <strong>Category 3 water damage</strong>—also called "black water"—contains harmful bacteria, viruses, and pathogens that pose serious health risks, especially in Merced's warm climate where bacteria multiply rapidly.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
              <span className="inline-flex items-center justify-center w-10 h-10 bg-teal-600 text-white rounded-full mr-3 text-lg">2</span>
              Stop the Water Source (Minutes 15–30)
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              If it's safe to do so, <strong>stop the flow of water immediately</strong>. This might mean:
            </p>
            <ul className="list-disc list-inside text-gray-700 leading-relaxed mb-6 space-y-2 ml-4">
              <li>Shutting off the main water valve to your home (usually located near the water meter or where the main line enters your house)</li>
              <li>Turning off the supply valve to a leaking appliance (washing machine, dishwasher, water heater)</li>
              <li>Placing a bucket under a small leak or using towels to contain minor drips</li>
              <li>Calling a licensed plumber if you can't locate or access the shutoff valve</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-6">
              Many Merced homeowners don't know where their main water shutoff is located until an emergency happens. Take a moment right now—before disaster strikes—to locate yours and make sure every adult in your household knows where it is.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
              <span className="inline-flex items-center justify-center w-10 h-10 bg-teal-600 text-white rounded-full mr-3 text-lg">3</span>
              Document Everything for Insurance (Minutes 30–60)
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Before you move or remove anything</strong>, document the damage thoroughly. Insurance companies require proof of loss, and the more evidence you provide, the stronger your claim will be.
            </p>
            <ul className="list-disc list-inside text-gray-700 leading-relaxed mb-6 space-y-2 ml-4">
              <li><strong>Take photos and videos</strong> of all affected areas, damaged belongings, and standing water. Capture wide shots and close-ups.</li>
              <li><strong>Make a written inventory</strong> of damaged items, including approximate age and value.</li>
              <li><strong>Save receipts</strong> for any emergency purchases (tarps, fans, cleaning supplies).</li>
              <li><strong>Note the time and date</strong> the damage occurred, if known.</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-6">
              Indoor-Restore Services works directly with all major insurance carriers serving Merced County. We provide detailed moisture mapping, thermal imaging, and comprehensive documentation to support your claim—at no extra cost to you.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
              <span className="inline-flex items-center justify-center w-10 h-10 bg-teal-600 text-white rounded-full mr-3 text-lg">4</span>
              Remove Standing Water (Hours 1–4)
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              The longer water sits, the more damage it causes. <strong>Begin water removal as quickly as possible.</strong> For small amounts of water (less than an inch), you can use:
            </p>
            <ul className="list-disc list-inside text-gray-700 leading-relaxed mb-6 space-y-2 ml-4">
              <li>Mops and towels to soak up water</li>
              <li>A wet/dry vacuum (shop vac) to extract water from carpets and hard floors</li>
              <li>Buckets to bail out standing water</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-6">
              However, for <strong>significant water damage</strong>—anything more than a small, contained spill—you need professional-grade extraction equipment. Indoor-Restore Services uses truck-mounted extractors and portable pumps capable of removing thousands of gallons per hour. We also use moisture meters and thermal imaging cameras to detect hidden water in walls, ceilings, and subfloors that you can't see with the naked eye.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
              <span className="inline-flex items-center justify-center w-10 h-10 bg-teal-600 text-white rounded-full mr-3 text-lg">5</span>
              Move and Protect Your Belongings (Hours 2–6)
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Once you've documented the damage, <strong>move furniture, rugs, and belongings to a dry area</strong>. Elevate furniture legs with aluminum foil or wood blocks to prevent further water absorption. Remove wet cushions, pillows, and fabrics and place them in a well-ventilated area to dry.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              For valuable items—electronics, important documents, family heirlooms—move them to a completely dry room or even off-site if necessary. In Merced's climate, humidity can cause secondary damage even to items that weren't directly soaked.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
              <span className="inline-flex items-center justify-center w-10 h-10 bg-teal-600 text-white rounded-full mr-3 text-lg">6</span>
              Begin Air Circulation and Dehumidification (Hours 4–12)
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Proper drying is the most critical step</strong> in preventing mold growth and structural damage. Open windows and doors (if outdoor humidity is lower than indoor humidity—check the weather in Merced before doing this). Turn on ceiling fans and place box fans or portable fans to create airflow across wet surfaces.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              If you have a dehumidifier, run it continuously in the affected area. However, understand that <strong>consumer-grade equipment has serious limitations</strong>. A typical home dehumidifier removes 30–50 pints of moisture per day. Professional restoration dehumidifiers remove 150–200+ pints per day and are specifically designed for structural drying.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              Indoor-Restore Services deploys industrial air movers and commercial-grade dehumidifiers strategically throughout your Merced home. We monitor moisture levels daily using advanced meters and adjust equipment placement to ensure complete, uniform drying—typically within 3 to 5 days.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
              <span className="inline-flex items-center justify-center w-10 h-10 bg-teal-600 text-white rounded-full mr-3 text-lg">7</span>
              Contact Your Insurance Company (Hours 6–12)
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Call your insurance company as soon as possible</strong>—ideally within the first 12 hours. Report the damage, provide your documentation, and ask about your coverage and deductible. Request a claim number and the name of your assigned adjuster.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              Many Merced homeowners are surprised to learn that their policy requires them to take "reasonable steps" to prevent further damage. This means you're expected to begin mitigation immediately—not wait days for an adjuster to visit. Failure to mitigate can result in claim denial for secondary damage like mold.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              Indoor-Restore Services can communicate directly with your insurance adjuster, provide estimates, and even bill your insurance company directly in many cases. We take the stress out of the claims process so you can focus on getting your life back to normal.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
              <span className="inline-flex items-center justify-center w-10 h-10 bg-teal-600 text-white rounded-full mr-3 text-lg">8</span>
              Call a Professional Restoration Company (Hours 1–24)
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              Here's the truth: <strong>most water damage situations require professional restoration</strong>. Even if you've removed standing water and set up fans, hidden moisture in walls, insulation, and subfloors will cause long-term problems if not properly addressed. Mold, wood rot, and structural instability can develop silently over weeks and months, costing you far more than the initial restoration would have.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              Indoor-Restore Services is <strong>IICRC-certified</strong> (the gold standard in restoration), fully licensed and insured, and available 24/7 for emergency response throughout Merced County. When you call us at <strong>(209) 451-2288</strong>, we'll be on-site within 60 minutes to assess the damage, begin water extraction, and create a customized restoration plan for your home.
            </p>

            {/* What NOT to Do */}
            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 flex items-center gap-3">
              <i className="ri-close-circle-line text-red-600"></i>
              What NOT to Do After Water Damage
            </h2>

            <div className="bg-red-50 border-l-4 border-red-600 p-6 rounded-lg mb-8">
              <ul className="space-y-3 text-gray-800">
                <li className="flex items-start gap-3">
                  <i className="ri-close-line text-red-600 mt-1 flex-shrink-0"></i>
                  <span><strong>Don't wait.</strong> Every hour of delay increases damage exponentially. Mold can begin growing in as little as 24 hours in Merced's climate.</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-line text-red-600 mt-1 flex-shrink-0"></i>
                  <span><strong>Don't use electrical appliances</strong> in wet areas. Electrocution is a real and deadly risk.</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-line text-red-600 mt-1 flex-shrink-0"></i>
                  <span><strong>Don't enter rooms with sagging ceilings.</strong> Water-saturated drywall and insulation can collapse without warning.</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-line text-red-600 mt-1 flex-shrink-0"></i>
                  <span><strong>Don't use your home vacuum</strong> to remove water. Standard vacuums are not designed for water and can be damaged or cause electrical shock.</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-line text-red-600 mt-1 flex-shrink-0"></i>
                  <span><strong>Don't assume it's dry</strong> just because surfaces feel dry. Moisture meters are essential to detect hidden water.</span>
                </li>
              </ul>
            </div>

            {/* When to Call a Professional */}
            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 flex items-center gap-3">
              <i className="ri-phone-line text-teal-600"></i>
              When to Call Indoor-Restore Services
            </h2>

            <p className="text-gray-700 leading-relaxed mb-4">
              You should call a professional water damage restoration company immediately if:
            </p>
            <ul className="list-disc list-inside text-gray-700 leading-relaxed mb-6 space-y-2 ml-4">
              <li>Water has affected more than one room</li>
              <li>Water has been standing for more than a few hours</li>
              <li>Water has soaked into carpets, padding, drywall, or insulation</li>
              <li>The water source is contaminated (sewage, toilet overflow, outdoor flooding)</li>
              <li>You see or smell mold</li>
              <li>You're unsure of the extent of the damage</li>
              <li>Your insurance company requires professional documentation</li>
            </ul>

            <p className="text-gray-700 leading-relaxed mb-6">
              Indoor-Restore Services serves all of Merced County, including Merced, Atwater, Los Banos, Livingston, Winton, Hilmar, and surrounding communities. We're locally owned and operated, and we understand the unique challenges Central Valley homeowners face—from seasonal flooding to aging plumbing in older Merced neighborhoods like Applegate Park and the North Side.
            </p>

            {/* FAQ Section */}
            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 flex items-center gap-3">
              <i className="ri-question-line text-teal-600"></i>
              Frequently Asked Questions
            </h2>

            <div className="space-y-6 mb-12">
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-100">
                <h4 className="text-lg font-bold text-gray-900 mb-3">
                  How quickly does mold grow after water damage in Merced, CA?
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  In Merced's warm Central Valley climate, <strong>mold can begin growing within 24 to 48 hours</strong> after water exposure. The combination of moisture and our region's heat creates ideal conditions for rapid mold colonization. This is why immediate water extraction and structural drying are essential—waiting even a day or two can turn a manageable water damage situation into a costly mold remediation project.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6 border border-gray-100">
                <h4 className="text-lg font-bold text-gray-900 mb-3">
                  Should I use my home's electricity after water damage?
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  <strong>No.</strong> If water has reached electrical outlets, appliances, or your breaker box, do not use electricity in affected areas. Turn off power at the main breaker if it's safe to do so, or call PG&E and a licensed electrician. Electrocution is a serious risk in flooded homes. Indoor-Restore Services works closely with certified electricians throughout Merced County to ensure your home is safe before restoration begins.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6 border border-gray-100">
                <h4 className="text-lg font-bold text-gray-900 mb-3">
                  Will my homeowner's insurance cover water damage in Merced?
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  <strong>It depends on the source.</strong> Most homeowner's policies in California cover sudden and accidental water damage—like burst pipes, appliance malfunctions, or roof leaks. However, flood damage from natural disasters typically requires separate flood insurance (NFIP or private flood policies). Indoor-Restore Services has extensive experience working with insurance companies serving Merced, Atwater, Los Banos, and Livingston. We provide detailed documentation, moisture readings, and photos to support your claim and maximize your payout.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6 border border-gray-100">
                <h4 className="text-lg font-bold text-gray-900 mb-3">
                  Can I dry out my Merced home myself with fans and dehumidifiers?
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  Consumer-grade fans and dehumidifiers are <strong>not sufficient for structural drying</strong>. Water penetrates deep into drywall, insulation, subfloors, and framing—areas you can't see or reach with household equipment. Professional restoration companies like Indoor-Restore Services use industrial-grade air movers, commercial dehumidifiers, and moisture meters to ensure complete drying. Incomplete drying leads to hidden mold growth, wood rot, and structural damage that can cost tens of thousands of dollars to repair later.
                </p>
              </div>
            </div>

            {/* Conclusion */}
            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
              Don't Face Water Damage Alone
            </h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Water damage is overwhelming, stressful, and time-sensitive. But you don't have to face it alone. <strong>Indoor-Restore Services</strong> has been helping Merced County families recover from water emergencies for years. We're IICRC-certified, fully insured, and available 24/7/365—including holidays and weekends.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              When you call us, you're not just getting water extraction and drying equipment. You're getting a dedicated team that will guide you through every step of the restoration process, work directly with your insurance company, and restore your home to pre-loss condition—or better.
            </p>
            <p className="text-gray-700 leading-relaxed mb-8">
              <strong>The first 24 hours matter.</strong> Don't wait. Call Indoor-Restore Services now at <strong>(209) 451-2288</strong> and let us help you protect your Merced home, your belongings, and your peace of mind.
            </p>
          </div>

          {/* Emergency CTA */}
          <div className="bg-gradient-to-br from-teal-700 to-teal-900 rounded-xl p-8 text-center mt-12">
            <div className="max-w-2xl mx-auto">
              <i className="ri-alarm-warning-line text-5xl text-teal-300 mb-4 block"></i>
              <h3 className="text-2xl font-bold text-white mb-3">
                Experiencing Water Damage Right Now?
              </h3>
              <p className="text-teal-100 mb-6 text-sm leading-relaxed">
                Our emergency response team is standing by 24/7 to help Merced County homeowners. We'll be on-site within 60 minutes to begin water extraction and damage mitigation.
              </p>
              <a
                href="tel:2094512288"
                className="inline-flex items-center gap-3 bg-white text-teal-700 px-8 py-4 rounded-lg font-bold text-lg hover:bg-teal-50 transition-colors whitespace-nowrap cursor-pointer shadow-lg"
              >
                <i className="ri-phone-line text-xl"></i>
                Call Now: (209) 451-2288
              </a>
              <p className="text-teal-200 text-xs mt-4">
                Available 24/7 • 60-Minute Response • Free Estimates
              </p>
            </div>
          </div>

          {/* Related Articles */}
          <div className="mt-16 pt-12 border-t border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Related Articles</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Link
                to="/blog"
                className="group bg-gray-50 rounded-lg p-6 border border-gray-100 hover:border-teal-200 hover:shadow-md transition-all cursor-pointer"
              >
                <span className="text-teal-600 text-xs font-semibold uppercase tracking-wide">Mold Prevention</span>
                <h4 className="text-lg font-bold text-gray-900 mt-2 mb-2 group-hover:text-teal-700 transition-colors">
                  How to Prevent Mold After a Flood
                </h4>
                <p className="text-gray-600 text-sm">Learn the proven strategies to stop mold before it starts.</p>
              </Link>
              <Link
                to="/blog"
                className="group bg-gray-50 rounded-lg p-6 border border-gray-100 hover:border-teal-200 hover:shadow-md transition-all cursor-pointer"
              >
                <span className="text-teal-600 text-xs font-semibold uppercase tracking-wide">Insurance Tips</span>
                <h4 className="text-lg font-bold text-gray-900 mt-2 mb-2 group-hover:text-teal-700 transition-colors">
                  Navigating Your Water Damage Insurance Claim
                </h4>
                <p className="text-gray-600 text-sm">Get the maximum payout you deserve with our step-by-step guide.</p>
              </Link>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}